/**
 * Pure view-model helpers for the Dhruva desk (unit-tested in dhruvaView.test.js).
 */

const n = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

export const inr = (v, digits = 2) => {
    const x = n(v);
    if (x === null) return '—';
    const s = Math.abs(x).toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits });
    return x < 0 ? `-₹${s}` : `₹${s}`;
};

const isToday = (iso, now = new Date()) => {
    if (!iso) return false;
    const fmt = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(d);
    const d = new Date(iso);
    return !Number.isNaN(d.getTime()) && fmt(d) === fmt(now);
};

/** Accounts present today: from layers (L1:<app>), day rows and journal legs. */
export function listAccounts(payload) {
    const set = new Set();
    (payload?.layers || []).forEach((l) => {
        const id = l.accountId || String(l.layerId || '').split(':')[1];
        if (id) set.add(id);
    });
    (payload?.dhruva?.days || []).forEach((d) => d.accountId && set.add(d.accountId));
    (payload?.dhruva?.legs || []).forEach((l) => l.accountId && set.add(l.accountId));
    return Array.from(set).sort((a, b) => (a === 'contra' ? -1 : b === 'contra' ? 1 : a.localeCompare(b)));
}

/**
 * One account's view: prefer the live layer (has LTP / stop state), fall back to the
 * journal-built legs when no live layer exists today (e.g. after a backend restart).
 */
export function accountView(payload, accountId, now = new Date()) {
    const dh = payload?.dhruva || {};
    const layer = (payload?.layers || []).find(
        (l) => (l.accountId === accountId || l.layerId === `L1:${accountId}`) && isToday(l.capturedAt, now),
    ) || null;
    const journalLegs = (dh.legs || []).filter((l) => l.accountId === accountId);
    const legs = layer?.legs?.length
        ? layer.legs.map((leg) => {
            const j = journalLegs.find((x) => x.leg === leg.leg) || {};
            return {
                ...j,
                ...leg,
                timeline: (leg.timeline && leg.timeline.length ? leg.timeline : j.timeline) || [],
            };
        })
        : journalLegs.map((l) => ({
            ...l,
            entryTime: l.entryAt,
            exitTime: l.exitAt,
            stopState: l.status === 'closed' ? 'closed' : null,
            pnl: l.pnlGross,
        }));
    const day = (dh.days || []).find((d) => d.accountId === accountId) || null;
    const dayEvents = (dh.dayEvents || []).filter((e) => e.account === accountId);
    return { layer, summary: layer?.summary || {}, legs, day, dayEvents, capturedAt: layer?.capturedAt || null };
}

export const decisionTone = (decision) => (
    decision === 'TRADE' ? 'success' : decision === 'SKIP' ? 'warning' : decision ? 'error' : 'default'
);

export function decisionLabel(decision, now = new Date()) {
    if (decision?.decision) return decision.decision;
    const t = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(now);
    return t < '09:39' ? 'WAITING (09:39)' : 'NO DECISION';
}

/** Plain description of what currently protects an open leg. */
export function stopDescription(leg) {
    if (!leg) return '—';
    if (leg.status === 'closed' || leg.stopState === 'closed' || leg.exitPrice != null) return 'Closed';
    const trig = inr(leg.stopTrigger);
    switch (leg.stopState) {
        case 'initial': return `Stop ${trig} (2× entry)`;
        case 'at_cost': return `At cost ${trig}`;
        case 'trailing': return `Trailing ${trig}`;
        default: return leg.stopTrigger != null ? `Stop ${trig}` : 'No stop yet';
    }
}

export function protectionNarrative(leg) {
    if (!leg || leg.exitPrice != null || leg.status === 'closed') return null;
    const trig = inr(leg.stopTrigger);
    if (leg.stopState === 'initial') {
        return `Protected by an exchange stop-loss at ${trig} — twice the entry price. If the premium doubles, the leg is bought back automatically.`;
    }
    if (leg.stopState === 'at_cost') {
        return `The other leg was stopped out, so this leg's stop sits at its entry price (${trig}). It can no longer lose money beyond charges; the stop starts trailing once the premium falls 20% below entry.`;
    }
    if (leg.stopState === 'trailing') {
        return `The premium has fallen well below entry, so the stop trails ₹10 behind every ₹10 fall. Current stop ${trig} locks in profit if the premium bounces.`;
    }
    return 'Waiting for the protective stop order to be confirmed by the exchange.';
}

/** Control Metrics for the desk (label/value/detail/tone/description as MetricMatrix expects). */
export function buildControlMetrics({ decision, summary = {}, legs = [], day, capturedAt, now = new Date() }) {
    const d = decision || {};
    const open = legs.filter((l) => l.exitPrice == null && l.status !== 'closed' && l.entryPrice != null);
    const closed = legs.filter((l) => l.exitPrice != null || l.status === 'closed');
    const qty = n(legs[0]?.qty ?? legs[0]?.quantity);
    const premium = n(summary.premiumCollected) ?? (legs.length ? legs.reduce((s, l) => s + (n(l.entryPrice) || 0), 0) : null);
    const netPnl = n(summary.netPnl) ?? n(day?.netPnl);
    const cap = n(summary.lossCap);
    const used = n(summary.lossCapUsed);
    const ageS = capturedAt ? Math.max(0, Math.round((now - new Date(capturedAt)) / 1000)) : null;
    const ce = legs.find((l) => l.leg === 'CE');
    const pe = legs.find((l) => l.leg === 'PE');
    return [
        {
            label: "Today's decision",
            value: decisionLabel(d.tradingDay ? d : null, now),
            tone: decisionTone(d.decision),
            description: 'One shared decision at 09:39 for every account: TRADE, SKIP (gate), NO_DATA (holiday/outage) or ERROR.',
        },
        {
            label: 'Gate ratio / threshold',
            value: d.ratio != null ? `${Number(d.ratio).toFixed(2)} / ${d.threshold != null ? Number(d.threshold).toFixed(2) : '—'}` : '—',
            tone: d.ratio != null && d.threshold != null ? (d.ratio > d.threshold ? 'error' : 'success') : 'default',
            description: 'Share of a full day\'s expected move already used by 09:37. Dhruva trades only when it is at or below the 70th percentile of all previous days.',
        },
        {
            label: 'Opening range vs expected move',
            value: d.orRange != null ? `${Math.round(d.orRange)} vs ${d.impliedMove != null ? Math.round(d.impliedMove) : '—'} pts` : '—',
            detail: d.refClose != null ? `09:37 close ${Number(d.refClose).toLocaleString('en-IN')}` : null,
            description: 'Index high − low from 09:15 to 09:37, against the one-day move implied by the ATM straddle.',
        },
        {
            label: 'Straddle · DTE',
            value: d.straddle != null ? `${inr(d.straddle)} · ${d.dte != null ? Number(d.dte).toFixed(1) : '—'} d` : '—',
            detail: d.expiry ? `Expiry ${d.expiry}` : null,
        },
        {
            label: 'Strikes sold',
            value: d.ceStrike ? `${d.ceStrike} CE / ${d.peStrike} PE` : '—',
            detail: d.base ? `Base ${Number(d.base).toLocaleString('en-IN')} ± ${Math.round(d.differential || 0)} (half the straddle)` : null,
        },
        {
            label: 'Premium collected',
            value: premium != null ? `${inr(premium)}/unit` : '—',
            detail: premium != null && qty ? `${inr(premium * qty, 0)} on ${qty} qty per leg` : null,
        },
        {
            label: 'Day P&L (net)',
            value: inr(netPnl, 0),
            tone: netPnl > 0 ? 'success' : netPnl < 0 ? 'error' : 'default',
            detail: summary.realizedPnl != null
                ? `Realized ${inr(summary.realizedPnl, 0)} · open ${inr(summary.unrealizedPnl, 0)} · charges ${inr(summary.chargesRealized, 0)}`
                : null,
            description: 'Mark-to-market after charges on closed legs.',
        },
        {
            label: 'Loss-cap headroom',
            value: cap != null ? `${inr(-(used || 0), 0)} of ${inr(-cap, 0)}` : '—',
            tone: cap && used != null ? (used / cap > 0.75 ? 'error' : used / cap > 0.4 ? 'warning' : 'success') : 'default',
            detail: summary.lots ? `₹${(cap / summary.lots).toLocaleString('en-IN')}/lot × ${summary.lots}` : null,
            description: 'All legs are closed at market if the day\'s loss reaches the cap.',
        },
        { label: 'CE stop', value: stopDescription(ce) },
        { label: 'PE stop', value: stopDescription(pe) },
        {
            label: 'Legs open / closed',
            value: legs.length ? `${open.length} / ${closed.length}` : '0 / 0',
        },
        {
            label: 'Mode · lots',
            value: summary.mode ? `${String(summary.mode).toUpperCase()} · ${summary.lots} lot${summary.lots === 1 ? '' : 's'}` : (day?.mode ? String(day.mode).toUpperCase() : '—'),
            tone: summary.mode === 'live' ? 'success' : 'default',
            detail: summary.exitTime ? `Square-off ${String(summary.exitTime).slice(0, 5)}` : null,
        },
        {
            label: 'Telemetry',
            value: ageS == null ? 'waiting' : ageS < 10 ? 'live' : `${ageS}s old`,
            tone: ageS == null ? 'default' : ageS < 10 ? 'success' : ageS < 60 ? 'warning' : 'error',
            description: 'Age of the last snapshot from the account process (sent every ~1.5 s while legs are open).',
        },
    ];
}

const toMs = (at) => {
    const t = new Date(at || 0).getTime();
    return Number.isNaN(t) ? 0 : t;
};

/** Merge leg timelines and day-level events into one chronological history. */
export function buildHistory(legs = [], dayEvents = []) {
    const items = [];
    legs.forEach((leg) => (leg.timeline || []).forEach((t) => items.push({
        ...t, leg: leg.leg, strike: leg.strike,
    })));
    dayEvents.forEach((e) => items.push({ at: e.at, code: e.code, text: e.text, leg: null }));
    return items
        .filter((i) => i.text)
        .sort((a, b) => (toMs(a.at) - toMs(b.at)) || (a.seq ?? 0) - (b.seq ?? 0));
}

export const CODE_LABELS = {
    entry: 'Entered',
    stop_placed: 'Stop placed',
    stop_loss: 'Stop-loss',
    stop_to_cost: 'Stop to cost',
    stop_trailed: 'Stop trailed',
    trailing_stop: 'Trailing exit',
    stop_at_cost: 'Stopped at cost',
    session_exit: '15:10 exit',
    loss_cap: 'Loss cap',
    kill_switch: 'Kill switch',
    naked_leg_guard: 'Naked-leg guard',
    stop_rejected: 'Stop rejected',
    stop_unfilled: 'Stop unfilled',
    stop_replaced: 'Stop refreshed',
    manual_takeover: 'Manual control',
    restart_resume: 'Restart',
    gate_skip: 'Skipped',
    margin_short: 'Margin short',
    missed_entry: 'Missed entry',
    entry_failed: 'Entry failed',
    feed_fallback: 'Price feed',
    buyback_failed: 'Buy-back failed',
    unclosed_at_close: 'Open at close',
    eod_check: 'EOD check OK',
    eod_check_failed: 'EOD check',
    closed_outside: 'Closed in Kite',
    prior_open_leg: 'Open from earlier',
};

export const codeTone = (code) => {
    if (['entry', 'stop_placed', 'stop_to_cost', 'stop_trailed', 'trailing_stop', 'session_exit', 'eod_check'].includes(code)) return 'success';
    if (['stop_loss', 'stop_at_cost', 'gate_skip', 'restart_resume', 'stop_replaced', 'feed_fallback', 'closed_outside'].includes(code)) return 'warning';
    if (['loss_cap', 'kill_switch', 'naked_leg_guard', 'stop_rejected', 'stop_unfilled', 'manual_takeover',
        'margin_short', 'entry_failed', 'missed_entry', 'buyback_failed', 'unclosed_at_close', 'eod_check_failed',
        'prior_open_leg'].includes(code)) return 'error';
    return 'default';
};
